/**
 * Faixa de status da chamada em andamento.
 *
 * Aparece acima do painel do usuário quando há uma sessão de voz ativa. Mostra
 * o canal, a qualidade da conexão e os controles de tela e câmera, para que
 * sair da chamada ou parar de transmitir não dependa de voltar até o canal.
 */

import { forwardRef, useRef, useState, type ReactNode } from 'react'
import { ConnectionState } from 'livekit-client'
import { AudioLines, MonitorUp, PhoneOff, Signal, Video, VideoOff } from 'lucide-react'
import { formatBitrate } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { useAppStore } from '@/store/app'
import { useVoiceStore } from '@/store/voice'
import { usePreferencesStore } from '@/store/preferences'
import { Tooltip } from '@/components/ui'
import { ScreenSharePicker } from '@/components/voice/ScreenSharePicker'
import { SoundboardTray } from '@/components/voice/SoundboardTray'

export function VoiceStatusPanel() {
  const channelId = useVoiceStore((s) => s.channelId)
  const connectionState = useVoiceStore((s) => s.connectionState)
  const connecting = useVoiceStore((s) => s.connecting)
  const screenSharing = useVoiceStore((s) => s.screenSharing)
  const cameraOn = useVoiceStore((s) => s.cameraOn)
  const stats = useVoiceStore((s) => s.stats)
  const disconnect = useVoiceStore((s) => s.disconnect)
  const startScreenShare = useVoiceStore((s) => s.startScreenShare)
  const stopScreenShare = useVoiceStore((s) => s.stopScreenShare)
  const toggleCamera = useVoiceStore((s) => s.toggleCamera)

  const channel = useAppStore((s) => (channelId ? s.channels[channelId] : null))
  const guild = useAppStore((s) => (channel ? s.guilds[channel.guildId] : null))
  const defaultPreset = usePreferencesStore((s) => s.video.defaultScreenPreset)

  /*
    Este botão começava a transmitir direto, com o perfil salvo, sem passar pela
    escolha. Na prática o mesmo gesto fazia coisas diferentes conforme o lugar
    de onde era acionado, e quem quisesse trocar o perfil precisava descobrir
    sozinho que só o botão da sala de voz perguntava.
  */
  const [escolhendoPerfil, setEscolhendoPerfil] = useState(false)
  const [bandejaAberta, setBandejaAberta] = useState(false)
  const botaoDaBandeja = useRef<HTMLButtonElement>(null)

  if (!channelId && !connecting) return null

  const connected = connectionState === ConnectionState.Connected
  const reconnecting = connectionState === ConnectionState.Reconnecting

  const quality = reconnecting
    ? { label: 'Reconectando', color: 'text-warning' }
    : connected
      ? stats.packetsLostPercent > 5
        ? { label: 'Instável', color: 'text-warning' }
        : { label: 'Conectado', color: 'text-success' }
      : { label: 'Conectando', color: 'text-content-muted' }

  return (
    <div className="shrink-0 bg-surface-deepest px-2 pt-2">
      <div className="rounded-lg bg-surface-base p-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Signal className={cn('size-3.5 shrink-0', quality.color)} />
              <span className={cn('text-[13px] font-semibold', quality.color)}>{quality.label}</span>
            </div>
            <p className="mt-0.5 truncate text-[11px] text-content-muted">
              {guild?.name ? `${guild.name} / ` : ''}
              {channel?.name ?? 'canal de voz'}
            </p>
          </div>

          <Tooltip content="Sair da chamada">
            <button
              type="button"
              onClick={() => void disconnect()}
              aria-label="Sair da chamada"
              className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-overlay text-content-secondary transition-colors hover:bg-danger hover:text-white"
            >
              <PhoneOff className="size-4" />
            </button>
          </Tooltip>
        </div>

        {connected ? (
          <>
            {/*
              Só os ícones, com o nome no tooltip.

              Com a palavra ao lado, cada botão precisava de metade da faixa e
              não sobrava espaço para um terceiro. O desenho de cada um já diz
              do que se trata, e quem tiver dúvida descobre passando o mouse.
            */}
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              <BotaoDaFaixa
                rotulo={screenSharing ? 'Parar de transmitir' : 'Tela'}
                ativo={screenSharing}
                cor="success"
                onClick={() => (screenSharing ? void stopScreenShare() : setEscolhendoPerfil(true))}
              >
                <MonitorUp className="size-4" />
              </BotaoDaFaixa>

              <BotaoDaFaixa
                rotulo={cameraOn ? 'Desligar câmera' : 'Câmera'}
                ativo={cameraOn}
                onClick={() => void toggleCamera()}
              >
                {cameraOn ? <VideoOff className="size-4" /> : <Video className="size-4" />}
              </BotaoDaFaixa>

              <BotaoDaFaixa
                ref={botaoDaBandeja}
                rotulo="Efeitos sonoros"
                ativo={bandejaAberta}
                onClick={() => setBandejaAberta((v) => !v)}
              >
                <AudioLines className="size-4" />
              </BotaoDaFaixa>
            </div>

            {/* Taxa de envio e latência dizem, num relance, se a chamada está
                ruim por causa da conexão de quem transmite. */}
            {stats.outboundBps > 0 ? (
              <div className="mt-2 flex items-center justify-between text-[10px] text-content-muted">
                <span>Enviando {formatBitrate(stats.outboundBps)}</span>
                {stats.roundTripMs > 0 ? <span>{stats.roundTripMs} ms</span> : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {bandejaAberta ? (
        <SoundboardTray ancora={botaoDaBandeja.current} onClose={() => setBandejaAberta(false)} />
      ) : null}

      <ScreenSharePicker
        open={escolhendoPerfil}
        defaultPreset={defaultPreset}
        onClose={() => setEscolhendoPerfil(false)}
        onSelect={(preset, fonte) => {
          setEscolhendoPerfil(false)
          void startScreenShare(preset, fonte)
        }}
      />
    </div>
  )
}

/**
 * Botão quadrado da faixa: só o ícone, com o nome no tooltip.
 *
 * O tooltip não é enfeite aqui, é o único lugar onde o nome aparece; por isso
 * ele também vira o rótulo acessível do botão.
 */
const BotaoDaFaixa = forwardRef<
  HTMLButtonElement,
  {
    rotulo: string
    ativo?: boolean
    /** Verde para transmissão, azul para o resto. */
    cor?: 'brand' | 'success'
    onClick: () => void
    children: ReactNode
  }
>(function BotaoDaFaixa({ rotulo, ativo, cor = 'brand', onClick, children }, ref) {
  return (
    <Tooltip content={rotulo}>
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        aria-label={rotulo}
        aria-pressed={ativo}
        className={cn(
          'flex h-8 items-center justify-center rounded-md transition-colors',
          ativo
            ? cor === 'success'
              ? 'bg-success text-white hover:bg-success/85'
              : 'bg-brand text-white hover:bg-brand-hover'
            : 'bg-surface-overlay text-content-secondary hover:bg-surface-hover hover:text-content-primary',
        )}
      >
        {children}
      </button>
    </Tooltip>
  )
})
